/**
 * API client functions for Budget to Project Import
 *
 * All functions use the service from frontend/src/api/service.ts
 */

import { _axios } from '../../../api/service';
import {
  TransferCandidate,
  ValidationResult,
  SimulationResult,
  ImportResult,
  WizardData
} from './types';

const BASE_URL = '/api/budget-module/project-import';

/**
 * Get list of budgets eligible for transfer
 *
 * @returns Promise with list of accepted budgets not yet imported
 */
export async function getTransferCandidates(): Promise<TransferCandidate[]> {
  const response = await _axios.get(`${BASE_URL}/transfer_candidates/`);
  return response.data;
}

/**
 * Validate budget and wizard data before transfer
 *
 * @param budgetId - Budget ID to validate
 * @param wizardData - Wizard form data
 * @returns Promise with validation results
 */
export async function validateTransfer(
  budgetId: number,
  wizardData: Partial<WizardData>
): Promise<ValidationResult> {
  const response = await _axios.post(
    `${BASE_URL}/${budgetId}/validate_transfer/`,
    wizardData
  );
  return response.data;
}

/**
 * Run simulation (dry-run) of budget transfer
 *
 * @param budgetId - Budget ID to simulate
 * @param wizardData - Wizard form data
 * @returns Promise with simulation results
 */
export async function simulateTransfer(
  budgetId: number,
  wizardData: Partial<WizardData>
): Promise<SimulationResult> {
  const response = await _axios.post(
    `${BASE_URL}/${budgetId}/simulate_transfer/`,
    wizardData
  );
  return response.data;
}

/**
 * Execute actual budget transfer
 *
 * @param budgetId - Budget ID to import
 * @param wizardData - Wizard form data
 * @returns Promise with import results including new project ID
 */
export async function executeTransfer(
  budgetId: number,
  wizardData: WizardData
): Promise<ImportResult> {
  const response = await _axios.post(
    `${BASE_URL}/${budgetId}/execute_transfer/`,
    wizardData
  );
  return response.data;
}
